/**
 * Every number that affects how the game *feels*, in one file.
 *
 * This exists because the real specification for this game is a three-year-old's reaction, which
 * can only be discovered by sitting next to her and changing numbers. Vite hot-reloads this module
 * without restarting the run, so suspension stiffness can be adjusted mid-drive. Resist the urge
 * to sprinkle magic numbers into the drive code - they become untunable the moment they are buried.
 *
 * Units: pixels and seconds at the world scale of PX_PER_METRE. Planck works in metres, so the
 * physics layer divides by PX_PER_METRE on the way in and multiplies on the way out.
 */

export const PX_PER_METRE = 100;

export const physics = {
  /** Fixed simulation step. Decoupled from render so a slow tablet and a fast iPad feel identical. */
  timeStep: 1 / 60,
  /** Cap on catch-up steps per frame, so a stall cannot cause a physics explosion. */
  maxStepsPerFrame: 5,
  gravity: 14, // m/s^2 - deliberately heavier than earth; makes landings read as weighty

  chassis: {
    mass: 4.0,
    friction: 0.3,
    restitution: 0.05,
    /**
     * Where the centre of mass sits, in metres, relative to the *midpoint between the axles* - not the
     * hull centroid, which is where this used to be measured from and is the wrong reference. A traced
     * hull covers overhangs (Rubble's blade, Zuma's fan), so its centroid can sit well forward of the
     * wheels: on Rubble it was 0.61m ahead of the axle midpoint on a 1.67m wheelbase, which put 68% of
     * the weight on the front wheel and left the car visibly nose-down while standing still.
     *
     * Measured from the axles, level at rest is x = 0 for every vehicle however it was traced, and the
     * small rearward bias below is a deliberate choice rather than an accident of the artwork.
     *
     * y is up, so a *negative* value is a low centre of mass - the thing that stops the car nose-diving
     * off crests and tumbling on landings. It was +0.18 (i.e. high) while the comment claimed low.
     */
    centreOfMassOffset: { x: -0.12, y: -0.16 },
    /**
     * How much of the vehicle's mass lives in the ballast rather than the hull.
     *
     * The ballast is what puts the centre of mass where we asked for it; the bigger its share, the
     * closer to the hull it can sit to do that, and the more sane the rotational inertia stays. Half
     * and half keeps the required position inside the bodywork for every vehicle in the game.
     */
    ballastShare: 0.5,
    angularDamping: 0.4,
  },

  wheel: {
    mass: 0.6,
    friction: 1.6,
    restitution: 0.05,
    /** Fallback radius in metres when a template has no explicit wheel size. */
    defaultRadius: 0.26,
  },

  /**
   * Box2D's WheelJoint is revolute-plus-spring-along-an-axis - i.e. exactly car suspension.
   * frequencyHz is the spring's natural frequency; dampingRatio 1.0 would be critically damped.
   */
  suspension: {
    frequencyHz: 4.0,
    /** Nearer critical damping, so the body settles instead of bouncing after a shove. */
    dampingRatio: 0.85,
    /**
     * Suspension travel, metres.
     *
     * @unused travel - a Box2D WheelJoint is a spring along an axis with no stop on it, so the wheels
     * have always had unbounded travel and this number has never bounded anything. Wiring it in means
     * `enableLimit` plus a lower and upper translation in `src/drive/vehicle.ts`; until then it is a
     * figure describing suspension the game does not have.
     */
    travel: 0.18,
  },

  drive: {
    maxMotorTorque: 20,
    /**
     * Top speed in metres per second, converted to a wheel speed per vehicle.
     *
     * Specified as a *linear* speed, not an angular one, because the six vehicles have wheels from
     * 0.27m to 0.53m in radius - and a fixed motor speed would have made the hovercraft half the speed
     * of the digger. She will switch between them constantly, so they must all feel the same.
     *
     * Deliberately slow: at the original 26 rad/s the car crossed the whole screen in under a second,
     * which reads as a blur rather than as driving and gives her no time to react to anything.
     */
    topSpeedMps: 4.7,
    reverseSpeedFactor: 0.55,
    /**
     * Throttle ramp, units/second. Slower attack than release: pulling away is gentle, stopping is
     * prompt. 4.0 reached full power in a quarter second, which arrived as a jolt - the chassis
     * squatted on its suspension and the whole screen kicked.
     */
    throttleAttack: 2.0,
    throttleRelease: 6.0,
    /**
     * What the motor holds when nothing is asked for - engine braking, not a handbrake.
     *
     * This was `brakeTorque: 14`, which held the wheels at a standstill: letting go of the pedal stopped
     * the car almost dead, and on a hill it parked her there. Both were wrong for the same reason - a car
     * that stops the instant you stop pushing it does not behave like a thing with weight, and a slope she
     * can stand still on is a slope she cannot feel. Now the car coasts, and on a hill it rolls back.
     */
    idleTorque: 3.5,
    /**
     * The gradient at which the wheels are let go completely.
     *
     * Engine braking cannot tell "coasting to a stop" from "parked on a hill" - it is the same torque
     * resisting the same rotation - so one number could either stop her in half a truck length on the flat
     * or let her roll back down a slope, and not both. Measured: at 3.5 N.m she coasts 2.6m and rolls back
     * 0.5m; at 1.1 she rolls back 3.5m and coasts 6m, which is most of the visible screen.
     *
     * So the ground decides. On the flat the engine holds; from this gradient upwards it holds nothing and
     * gravity has the car. A gameplay rule rather than a physical one, and it matches what she sees: a car
     * rolls to a stop on the road, and a car left on a hill rolls down it.
     */
    rollFreeSlope: 0.08,
    /**
     * How fast she may roll backwards down a hill with nothing asked for, in m/s.
     *
     * Rolling back is the point; sledging is not. Above this the car bleeds speed, so a long slope
     * trundles her back down instead of running away. Not an assist level - a cap, and it only engages
     * above a speed she has no way of controlling.
     */
    rollbackCapMps: 2.0,
  },

  /** Nothing here is a penalty. Every entry exists to make failure impossible. */
  antiFrustration: {
    /** Extra angular damping applied while airborne, so the car cannot tumble. */
    airborneAngularDamping: 6.0,
    /** Beyond this tilt (radians) a gentle righting torque is applied. */
    autoRightTiltThreshold: 0.9,
    autoRightTorque: 6.0,
    /**
     * Seconds upside-down before the car puffs and uprights itself in place.
     *
     * In place, exactly: there was a `recoverRewind` here for shifting her back along the route as part of
     * the rescue, and nothing ever read it - the automatic recovery has always uprighted where she was. The
     * reset button did rewind 1.2m, which arrived as the car jumping backwards, and it does not any more.
     * Backing off something is what the reverse pedal is for.
     */
    recoverAfterSeconds: 1.5,
  },

  camera: {
    followLerp: 6.0,
    /** How far ahead of the car the camera leads, metres. Flips sign on the return leg. */
    lookAhead: 2.2,
    lookAheadLerp: 3.0,
    /** ~14 metres visible at the design frame, so the truck is about a third of the width. */
    baseZoom: 0.95,
    /**
     * Zoom-out at top speed. Disabled: at these speeds it was a continuous, barely-perceptible scale
     * change that made the whole scene feel like it was breathing, and it defeats any caching of the
     * terrain geometry. A steady scale reads as calmer.
     */
    speedZoomOut: 0,
    /**
     * How far above the car the camera sits, in metres.
     *
     * @unused verticalBias - the drive camera centres on the car and has never read this. Either wire it
     * in `src/drive/drive-screen.ts` or drop it; what it must not do is sit here looking like the reason
     * the framing is what it is.
     */
    verticalBias: -0.6,
  },
} as const;

/**
 * The trailer, always hitched.
 *
 * It is part of her car rather than a mission prop: hitched on every drive including free play, because
 * a trailer that turns up only on the runs that need one is a rule she has to learn, and a trailer that
 * is always there is just what her car looks like.
 *
 * Every number here is held down by the same constraint. The car she drew has a breakover slope, an
 * anti-flip torque, a centre of mass and an auto-righting recovery, all tuned against its own geometry -
 * and a heavy trailer re-tunes all four of them by accident. So the trailer is light, short, and has one
 * axle, and none of those is a shortcut: see the notes.
 */
export const trailer = {
  /**
   * Trailer mass as a fraction of the chassis's.
   *
   * A tenth, not a third. At a third the digger's front wheel lifts on a crest and the whole
   * `centreOfMassOffset` tuning - which is what stops the car standing on its tail - is being fought by
   * something behind it. A tenth is a thing she can see and feel and which changes no number above.
   */
  massShare: 0.1,
  /** And how much of the trailer's own weight is in its wheel. */
  wheelMassShare: 0.3,
  /**
   * How much of the trailer's body weight sits down at the axle rather than up in the bed.
   *
   * This is the number that stops it turning over, and it is not a small effect. The bed has to be raised
   * clear of the wheel or its front lip catches on every prop, which puts the whole body mass a good 20cm
   * above the pin - and a mass above the pin it hangs from is an inverted pendulum. Measured: the trailer
   * went over onto its back within twenty metres on five of the six recipes and was then dragged along
   * upside down for the rest of the drive.
   *
   * With most of the weight at the axle the centre of mass sits *below* the pin and the thing hangs the
   * way a trailer hangs. Same trick as the chassis's own ballast fixture, for the same reason.
   */
  ballastShare: 0.72,
  /**
   * Deck length, metres.
   *
   * There is still only one axle, and that is a safety property rather than a saving: breakover - a hull
   * grounding across a crest with both wheels in the air - needs two axles with a hull spanning between
   * them, so a single-axle deck has no breakover of its own however long it is and `terrain.maxSlope` keeps
   * its derivation from the car alone.
   *
   * The length itself is a *look*, and it was 1.1 until the trailer was seen on a tablet. `wheelRadiusShare`
   * has to be near the car's own wheel for the trailer to climb what the car climbs, and on the firetruck
   * that is a metre across - against a 1.1m bed it read as a wheelbarrow with a box on it. At 1.4 the wheel
   * is under a bed rather than beside one, and the pin still holds: 1.6 was tried and put too much overhang
   * either side of a single central axle, which the hitch felt as a moment on rough ground.
   *
   * Changing this means redrawing `trailers/flatbed.svg` to match. `check:vehicles` will say so.
   */
  deckLengthM: 1.4,
  deckHeightM: 0.22,
  /** Sides low enough to see the load over the top of. Drawn only; nothing collides with them. */
  sideHeightM: 0.26,
  /** Hitch pin to the leading edge of the deck. */
  drawbarM: 0.34,
  /** How far behind the rear bumper the pin sits, when the template does not say. */
  hitchOverhangM: 0.12,
  /**
   * How far the trailer may pitch relative to the car, radians. 1.75 is 100 degrees.
   *
   * A somersault backstop, and nothing narrower. It was 0.95 on the reasoning that a crest puts 33 degrees
   * between the two and a mound face needs 45, so 54 was that plus margin - and that reasoning is about
   * *ground*, which is not what the trailer swings on. Measured over every recipe: ordinary driving keeps
   * it inside 0.4, and then she hits a 1.1-metre ramp, the whole rig flies, and the trailer swings through
   * 1.6 before hanging back down a second later.
   *
   * At 0.95 that swing slammed into the limit on every jump, which is not a backstop - it is a metre-long
   * lever on the back of a car in mid-air, and the car's whole anti-tumble tuning assumes nothing is
   * levering it. So the limit is set where only a full somersault reaches it, and what actually keeps the
   * trailer the right way up is `comBelowPinM`: the weight hangs below the pin, so it swings back like a
   * pendulum instead of needing to be held. `tests/trailer.test.ts` measures both.
   */
  hitchAngleLimit: 1.75,
  /**
   * Trailer wheel radius as a fraction of the car's.
   *
   * The car's own, near enough, and that is a safety property rather than a look. `MAX_PROP_SLOPE` is
   * documented as "comfortably under the breakover angle of every vehicle - the hovercraft's 0.27m wheels
   * are the limiting case", and the trailer drives over exactly the same logs and rocks the car does. A
   * smaller wheel is outside the guarantee the props were shaped against: at 0.55 the trailer met a 0.43m
   * rock at full speed, went over it rather than up it, and landed on its back.
   */
  wheelRadiusShare: 0.92,
  /**
   * How far below the pin the trailer's centre of mass sits, metres.
   *
   * A positive number here is what makes the trailer a pendulum rather than an inverted one. The ballast is
   * placed to land the centre of mass exactly this far under the line it hangs from, whatever the bed
   * height worked out to for this car's wheels.
   */
  comBelowPinM: 0.06,
  /**
   * Its own suspension, stiffer than the car's 4Hz because the sprung mass is a tenth as big.
   *
   * A rigid axle would be cheaper and is the wrong trade: at the roughness a hard route reaches, a rigid
   * trailer axle sends every bump straight up the drawbar and into the chassis, and how the car feels is
   * the one thing this whole feature may not change.
   */
  suspensionHz: 5,
  suspensionDamping: 0.9,
  /** Keeps it from wagging about the pin over rough ground. */
  angularDamping: 0.6,
  friction: 0.5,
  restitution: 0.02,
  /** How big an item looks sitting in the bed, metres. */
  slotSizeM: 0.34,
  /** How far apart the load stacks, along the deck and upwards. */
  slotSpacingM: 0.38,
  slotRiseM: 0.3,
} as const;

/**
 * The assist ladder. Level 3 is the default for a three-year-old; level 0 is where a five-year-old
 * ends up. Stored in settings, changed only behind the parent gate.
 *
 * `autoThrottle` is off at every level, deliberately. An earlier version had the car drive itself
 * toward the goal at level 3 so she could never be stuck - but that removes the only thing she is
 * actually doing. Holding the pedal *is* the game. "Never stuck" is covered instead by the stall nudge
 * in Vehicle.applyAntiFrustration, which does nothing until the car is genuinely wedged.
 *
 * The mechanism is kept rather than deleted: if she turns out to be unable to hold a pedal and watch
 * the screen at once, it is one number away.
 *
 * `hasReverse` is now true at every level. It was withheld at 2 and 3 on the theory that one pedal is
 * simpler than two, and that was the wrong trade: a car she can only drive forwards is a car she cannot
 * get out of anything with, so her one control became "go, and hope". The flag stays because the argument
 * might come back for a younger child than this one.
 *
 * @unused hasBoost tiltSteering - neither is built. There is no boost pedal, and steering is by pedal
 * rather than by tilting the tablet, which was ruled out early: holding a tablet level is not a thing a
 * three-year-old does. Listed here rather than at each of the four levels so the ladder stays readable.
 */
export const assist = {
  3: {
    autoThrottle: false,
    /** Fraction of full throttle the car would apply on its own, if autoThrottle were enabled. */
    autoThrottleLevel: 0.72,
    hasReverse: true,
    hasBoost: true,
    autoBrakeOnDescent: true,
    tiltSteering: false,
  },
  2: {
    autoThrottle: false,
    autoThrottleLevel: 0,
    hasReverse: true,
    hasBoost: true,
    autoBrakeOnDescent: true,
    tiltSteering: false,
  },
  1: {
    autoThrottle: false,
    autoThrottleLevel: 0,
    hasReverse: true,
    hasBoost: false,
    autoBrakeOnDescent: false,
    tiltSteering: false,
  },
  0: {
    autoThrottle: false,
    autoThrottleLevel: 0,
    hasReverse: true,
    hasBoost: false,
    autoBrakeOnDescent: false,
    tiltSteering: true,
  },
} as const;

export type AssistLevel = keyof typeof assist;

/**
 * Journey lengths. Sized from the measured top speed so a leg takes 15-20 seconds: long enough to
 * feel like going somewhere, comfortably inside the 90-second budget a phase gets.
 *
 * TODO(M2): superseded by RouteDef, which carries length along with hilliness and surfaces.
 *
 * @unused goalDistanceM groundMarginM - the game's routes come from `RouteDef` and nothing under `src/`
 * reads either of these any more. They are not dead weight: `tests/vehicle-sim.test.ts` sizes its scratch
 * route from them, so the drivability sims run at the length and run-off the hand-built route used.
 * Delete them and that scratch route needs numbers of its own instead.
 */
export const route = {
  goalDistanceM: 60,
  /** Free play has no goal, so it just needs to be long enough never to feel like a box. */
  freePlayLengthM: 180,
  /** Flat run-off either side of the start and the goal. */
  groundMarginM: 12,
  /**
   * Invisible walls at both ends of the ground. Without them she drives off the end of the world and
   * falls forever - which she cannot recover from and which looks, to her, like the game breaking.
   */
  wallHeightM: 6,
} as const;

export const terrain = {
  /** Horizontal spacing of heightfield samples, pixels. */
  sampleSpacing: 32,
  /**
   * THE toddler-safety invariant: no terrain segment may be steeper than this (rise/run).
   *
   * 0.30 is about 17 degrees. The number is NOT a feel choice - it is derived from the vehicle's
   * breakover angle, i.e. ground clearance over half the wheelbase. The first version guessed 0.55
   * ("about 29 degrees, looks fine") and the car promptly beached on its belly at a crest, wheels in
   * the air, stuck for good on ground the clamp had declared safe. `breakoverSlope()` in
   * src/drive/vehicle.ts computes the real limit and a test asserts this stays comfortably under it.
   */
  maxSlope: 0.3,
  /**
   * Flat, safe ground either side of the Lookout and the goal item, and how deep the ground is drawn.
   *
   * @unused flatMarginPx groundThicknessPx - superseded by `RouteDef`, which carries the flat margins per
   * recipe (see `src/core/world/route-gen.ts`). Same story as `maxSlope`'s note above: the route owns the
   * shape of the world now, and these are what the single hardcoded route used before it did.
   */
  flatMarginPx: 420,
  groundThicknessPx: 600,
} as const;

export const draw = {
  /**
   * A three-year-old's tap lands *near* what she meant, not on it. On a miss we spiral outward
   * looking for a paintable region. Without this the studio reads as broken; with it, magic.
   */
  tapForgivenessPx: 24,
  tapForgivenessRings: 3,
  tapForgivenessSamplesPerRing: 8,
  /** Bake resolution, chosen per device tier at boot. Vector source, so this is a free parameter. */
  bakeWidthLow: 1024,
  bakeWidthHigh: 2048,
  /**
   * Drag-to-draw. With it off, a drag simply behaves like a tap, so nothing she presses changes meaning
   * halfway through.
   */
  crayonEnabled: true,
  /**
   * Crayon stroke width, in template units.
   *
   * This was 22, which is 4.4% of the car's height and *half the height of the smallest paint panel* -
   * so a single pass filled a panel solid and there was no way to make a small mark. Measured against
   * the art rather than chosen by eye, the same way `terrain.maxSlope` is.
   */
  strokeWidth: 12,
  /**
   * How much each slice of a mark varies in width, as a share of `strokeWidth`.
   *
   * Read by `strokeRibs` in src/core/paint/stroke.ts, which cuts a mark into overlapping round-capped
   * slices at slightly different widths - the bulges at the joins are what stops the mark reading as
   * vinyl tape. This tunable existed for a long time claiming to give "a waxy edge" while nothing read
   * it at all, which is a worse kind of wrong than a bad value.
   */
  strokeWidthJitter: 0.18,
  /**
   * Shortest mark worth keeping, in template units.
   *
   * Below this it was a twitch, not a drawing. At the old hardcoded 6 a three-point tick survived as a
   * nub that read as a spilled blob, and tapered ends make a very short mark look more like an accident
   * rather than less.
   */
  minStrokeLength: 26,
} as const;

/**
 * The emergency lights, on the horn button.
 *
 * One press, two payoffs: the two-tone sounds and the bar starts flashing, and it stays flashing for the
 * rest of the leg. There is no second button, because a small button beside the horn is a button she
 * presses by accident and then cannot explain. Keeping the horn held escalates - faster flash, siren
 * climbing with it - which is the one thing the button does that has to be discovered rather than found.
 */
export const lights = {
  /**
   * Light onsets a second across the whole fixture, with nothing held.
   *
   * Under three, the general photosensitivity threshold, and deliberately so: a flashing light pointed at
   * a three-year-old is not the place to run a number to its limit. It is also about the rate a real
   * rotating beacon turns at, which is presumably not a coincidence.
   */
  calmFlashesPerSecond: 1.5,
  /**
   * And while she keeps the horn held down.
   *
   * This one is over three, which is a deliberate exception rather than an oversight, on three grounds.
   * The threshold is written for flashes covering a quarter of the central visual field; the lit patch
   * here is about 0.2% of the screen (Chase's bar half is 97x46 units of a 1024x580 template, and the
   * whole truck is roughly a third of the viewport at the drive camera). It happens only while she is
   * actively holding the button, so it is something she is doing rather than something happening at her.
   * And `reducedMotionPulsesPerSecond` opts out of flashing altogether on any device that asks.
   *
   * Do not raise it further, and do not make it the resting rate.
   */
  urgentFlashesPerSecond: 4,
  /**
   * Slow breathes a second instead, on a device that has asked for less motion.
   *
   * Not "the same flash, slower" - a slow flash is still a flash. The lamp never goes dark at this
   * setting, so there is no onset to count, and holding the horn does not speed it up.
   */
  reducedMotionPulsesPerSecond: 0.5,
  /** Share of each lamp's turn it is lit. The rest is the dark gap that makes it read as a flash. */
  flashDuty: 0.62,
  /** How long the escalation takes to arrive, and how long it takes to settle back afterwards. */
  urgencyAttackS: 0.45,
  urgencyReleaseS: 1.1,
  /**
   * How long she must hold before anything escalates, seconds.
   *
   * The length of the horn's own honk (`horn()` in src/audio/audio.ts), so a tap gives the two-tone and
   * one calm flash with nothing competing, and the siren only ever starts after the honk has finished.
   */
  hornTailS: 0.46,
  /**
   * Where each lamp of a bar sits across it, as a share of the box - and, doubled, how wide its lens is.
   *
   * Measured off the art rather than chosen. All three shipped bars are drawn as two outer lenses either
   * side of a central housing, with the dividers on the lines layer, and their outer lens centres sit at
   * 0.134 (Chase), 0.1375 (Marshall) and 0.131 (Tracker) of the box. So one number lands every lamp on its
   * own authored lens, and twice it is that lens's width to within two pixels on all three.
   *
   * It was 0.25 first, which is the tidy answer and the wrong one: it put each glow squarely on a divider
   * stroke rather than on a lens.
   */
  lampInset: 0.135,
  /**
   * Peak opacity of the lens - the layer that tints her paint to the lamp's colour.
   *
   * This layer exists because additive light cannot brighten white, and an unpainted light bar is white:
   * with the bloom alone the red half was a faint haze on the default car and the blue half was invisible.
   * Under one, and by a margin, so whatever she painted the bar still reads through the lit lens - the same
   * bargain the windscreen takes at its own alpha.
   */
  lensAlpha: 0.78,
  /** Peak brightness of the additive bloom above it - the light in the air, not the glass. */
  glowAlpha: 0.85,
  /**
   * How far the glow spills past the region's box, as a share of the box.
   *
   * Applied once across a bar and twice up it: light spilling off a roofline is what says "this is
   * emitting", where spilling sideways only smears the two halves into one wide blur.
   */
  glowSpill: 0.55,
  /** How much a beacon dome swells at the top of its pulse, so it reads as turning rather than blinking. */
  beaconSwell: 0.18,
  /** Dimmest a breathing lamp gets under reduced motion. Above zero, so nothing ever flashes. */
  reducedMotionFloor: 0.3,
} as const;

export const ui = {
  /**
   * Hard requirement: any tap produces visible and audible feedback within this window. And the floor
   * on a touch target, in CSS pixels.
   *
   * @unused feedbackBudgetMs minTapPx - both are specifications rather than knobs, and nothing reads
   * either. `--tap-min` in style.css carries the 120 independently, which is a second copy waiting to
   * disagree. `docs/MENUS.md` section 7 names the consumer these are waiting for: a Playwright pass
   * asserting every menu button's box at two viewport sizes, which jsdom cannot do because it has no
   * layout engine.
   */
  feedbackBudgetMs: 100,
  minTapPx: 120,
  minPedalPx: 180,
  /** The PupPad accepts itself if she does not tap, so no screen can strand her. */
  briefingAutoAcceptSeconds: 6,
  /**
   * Idle before a translucent helper paw points at what to tap, and before the pup does something funny.
   *
   * @unused idleHintSeconds idleAmuseSeconds - the helper paw and the idle amusement are both unbuilt.
   * They are in `docs/DESIGN.md` under the toddler UX rules; the numbers came first and the features
   * never followed.
   */
  idleHintSeconds: 12,
  idleAmuseSeconds: 20,
  /**
   * @unused screenFadeMs - the 220ms screen fade is done in CSS (`docs/MENUS.md` calls it the motion
   * budget), so this is a duplicate of a number that lives in the stylesheet. Delete it or make the
   * stylesheet read it; do not leave two.
   */
  screenFadeMs: 220,
  collectBeatSeconds: 2.2,
  arriveBeatSeconds: 2.6,
} as const;

export const progression = {
  pointsPerMission: 10,
  /**
   * Extra points for a bigger load, indexed by how many things she fetched.
   *
   * A four-item run paying the same as a two-item run is a lesson in not bothering. But it must not scale
   * linearly either - the jar is not an economy, and a three-year-old who cannot count to four is certainly
   * not doing arithmetic about which errand pays best. Four items is +40% rather than +100%: enough that
   * the jar visibly jumps further, not enough to be a choice.
   *
   * Indexed rather than multiplied so the shape is visible at a glance and a zero-item state - which the
   * game does not produce, but a forced one could - costs nothing.
   */
  loadBonusPoints: [0, 0, 2, 3, 4],
  /**
   * @unused pointsPerRoadsideTreat - there are no roadside treats to collect. The only thing on a route
   * she picks up is the mission item, and that scores through `pointsPerMission`.
   */
  pointsPerRoadsideTreat: 1,
  /**
   * Front-loaded on purpose: delayed gratification is not a three-year-old feature.
   *
   * @unused earlyUnlockEveryNMissions - superseded by the unlock cost table in
   * `src/core/game/unlock-rules.ts`, which sets the same intent directly: the first prize lands after the
   * very first mission, and no gap in the first dozen exceeds four. A test enforces that, which this
   * number never did.
   */
  earlyUnlockEveryNMissions: 2,
} as const;
